import { FC, useEffect, useState } from "react";
import TagIcon from "@Icons/TagIcon";
import OfferChevronRight from "@Icons/OfferChevronRight";
import styles from "./OffersSectionStyles.module.scss";
import PropertyDetailsModal from "../PropertyDetailsModal";
import gtagEventEmitter from "helpers/gtagEventEmitter";
import { PAGE } from "constants/configConstants";
import replaceAmberDomainInLinks from "@Utils/clientUtils/dangerouslySetInnerHtmlUtility/replaceAmberDomainInLinks";
import wrapperHOC from "@Utils/wrapperHOC";
import Portal from "@Components/Portal";
import useTranslationHook from "hooks/useTranslationHook";
import { AppPromotionContent } from "../ListItem/components/AppPromotionContent";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";

interface OfferItemProps {
  title: string;
  content: React.ReactNode;
  onClose?: () => void;
  shouldOpen?: () => boolean;
  defaultOpen?: boolean;
  gtagEventAction?: string;
  gtagExtraParams?: any;
  isDesktop?: boolean;
  tnc?: React.ReactNode;
  code?: string;
  isInternalUser?: boolean;
  labelPrefix?: string;
  expiryInfo?: { text: string; isUrgent: boolean } | null;
  action_type?: string;
}

const OfferItem: FC<OfferItemProps> = ({
  title,
  content,
  onClose,
  shouldOpen,
  defaultOpen = false,
  gtagEventAction,
  gtagExtraParams = {},
  isDesktop = false,
  tnc,
  code,
  isInternalUser,
  labelPrefix,
  expiryInfo,
  action_type,
}) => {
  const [openModal, setOpenModal] = useState(defaultOpen);
  const { t } = useTranslationHook();

  useEffect(() => {
    if (defaultOpen) {
      gtagEventEmitter({
        action: `viewed_${gtagEventAction}`,
        category: PAGE.INVENTORY,
        label: title,
        ...gtagExtraParams,
      });
    }
  }, []);

  const handleCloseListItem = () => {
    setOpenModal(false);
    if (typeof onClose === "function") {
      onClose();
    }
  };

  const handleOpenListItem = (e: any) => {
    e.preventDefault();

    if (shouldOpen && !shouldOpen()) {
      return;
    }

    setOpenModal(true);
    gtagEventEmitter({
      action: `viewed_${gtagEventAction}`,
      category: PAGE.INVENTORY,
      label: title,
      ...gtagExtraParams,
    });
  };

  const formattedTitle = title.split("_").join(" ");

  // Build display title with optional label prefix
  const displayTitle = labelPrefix ? (
    <>
      <span className={styles.offerLabelPrefix}>{labelPrefix}:</span> {formattedTitle}
    </>
  ) : (
    formattedTitle
  );

  return (
    <>
      <div
        className={styles.offerItem}
        onClick={handleOpenListItem}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleOpenListItem(e);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <div className={styles.offerIconWrapper}>
          <TagIcon />
        </div>
        <div className={styles.offerContent}>
          <span className={styles.offerTitle}>{displayTitle}</span>
          {expiryInfo && (
            <span className={formatClassNames(styles.expiryBadge, isDesktop && styles.desktop)}>
              {expiryInfo.text}
            </span>
          )}
        </div>
        <div className={styles.offerArrow}>
          <OfferChevronRight width={12} height={12} />
        </div>
      </div>

      {openModal && (
        <Portal>
          <PropertyDetailsModal
            header={formattedTitle}
            actionButtonText="Got it"
            hidePropertyModal={handleCloseListItem}
            isDesktop={isDesktop}
          >
            {isInternalUser && code && (
              <div className={styles.agentCodeBox}>
                <span className={styles.agentCodeLabel}>OFFER CODE FOR AGENT:</span> {code}
              </div>
            )}

            <div className={styles.offerModalContent}>
              {typeof content === "string" ? (
                <div dangerouslySetInnerHTML={{ __html: replaceAmberDomainInLinks(content) }} />
              ) : (
                content
              )}
            </div>

            {action_type?.trim()?.toLowerCase() === "install_app" && isDesktop ? (
              <AppPromotionContent />
            ) : (
              tnc && (
                <div className={styles.offerModalContent}>
                  <h4 className={styles.offerModalHeading}>
                    {t("propertypage.components.listitem.termsandconditions")}
                  </h4>
                  {typeof tnc === "string" ? (
                    <div dangerouslySetInnerHTML={{ __html: replaceAmberDomainInLinks(tnc) }} />
                  ) : (
                    tnc
                  )}
                </div>
              )
            )}
          </PropertyDetailsModal>
        </Portal>
      )}
    </>
  );
};

OfferItem.displayName = "OfferItem";

export default wrapperHOC(OfferItem, {
  componentName: "OfferItem-OffersSection-PropertyPage",
  showForChina: true,
});
